import axios, { InternalAxiosRequestConfig } from 'axios';
import { getStoredToken, clearAuthData } from '@/lib/auth';
import { useUIStore } from '@/contexts/uiStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean;
};

let sessionValidationPromise: Promise<boolean> | null = null;

function isAuthSessionRequest(url?: string): boolean {
  return Boolean(url?.includes('/auth/me'));
}

function isAuthCredentialRequest(url?: string): boolean {
  return Boolean(url?.includes('/auth/login') || url?.includes('/auth/register'));
}

async function validateStoredSession(): Promise<boolean> {
  const token = getStoredToken();
  if (!token) return false;

  try {
    await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return true;
  } catch {
    return false;
  }
}

function getSessionValidation(): Promise<boolean> {
  if (!sessionValidationPromise) {
    sessionValidationPromise = validateStoredSession().finally(() => {
      sessionValidationPromise = null;
    });
  }

  return sessionValidationPromise;
}

// Request interceptor - attach JWT token from localStorage
axiosClient.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 and 402
// Note: We only clear auth data here. Navigation is handled by React Router
// through the ProtectedRoute component reacting to auth state changes.
axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config as RetriableRequestConfig | undefined;
    const requestUrl = originalRequest?.url;

    if (status === 401) {
      const hasStoredToken = Boolean(getStoredToken());

      if (isAuthSessionRequest(requestUrl)) {
        clearAuthData();
        return Promise.reject(error);
      }

      if (
        hasStoredToken &&
        originalRequest &&
        !originalRequest._authRetry &&
        !isAuthCredentialRequest(requestUrl)
      ) {
        const sessionIsValid = await getSessionValidation();

        if (sessionIsValid) {
          originalRequest._authRetry = true;
          originalRequest.headers.Authorization = `Bearer ${getStoredToken()}`;
          return axiosClient(originalRequest);
        }

        clearAuthData();
      }

      if (!hasStoredToken) {
        clearAuthData();
      }
    }

    if (status === 402) {
      // Payment required - trigger global upgrade modal
      const detail = error.response?.data?.detail;

      // Extract structured error data if available
      const reason =
        typeof detail === 'object'
          ? detail.message
          : detail || 'Upgrade necessário para continuar';
      const currentUsage = typeof detail === 'object' ? detail.current : undefined;
      const limit = typeof detail === 'object' ? detail.limit : undefined;
      const tier = typeof detail === 'object' ? detail.tier : undefined;

      // Trigger global upgrade modal
      useUIStore.getState().openUpgradeModal({
        reason,
        currentUsage,
        limit,
        tier,
      });
    }

    return Promise.reject(error);
  }
);
